// Persisted tenant (Auth0 organization) selection, read synchronously at
// startup by `AuthProvider` to scope the very first token.
//
// The storage backend is host-dependent:
// - Cloudflare Pages previews ("*.pages.dev") mint a fresh origin per branch
//   deploy, and localStorage is keyed per-origin — so every preview would start
//   org-less and force the email/org-discovery step again. We instead keep the
//   org id in a cookie scoped to the Pages *project* domain ("<project>.pages.dev",
//   a public-suffix registrable domain), which every branch-preview subdomain
//   shares. That lets Auth0 finish login by a silent redirect. The org id is not
//   a secret, so a readable cookie is fine; the token cache itself is NOT shared
//   (it stays in per-origin localStorage — a redirect per new origin is expected).
// - Everywhere else (dev/prod/localhost) keeps localStorage, matching Auth0's
//   own `localstorage` cacheLocation so the org and its cached token co-locate.
const KEY = "appretio.selected_org_id";

/**
 * On a Cloudflare Pages host, the registrable domain to scope the shared cookie
 * to ("<project>.pages.dev"); `null` elsewhere (dev/prod/localhost), which
 * selects the localStorage backend. `pages.dev` is a public suffix, so the last
 * three labels form the registrable domain a cookie may span.
 */
function pagesProjectCookieDomain(): string | null {
  try {
    const host = window.location.hostname;
    if (!host.endsWith(".pages.dev")) {
      return null;
    }
    const labels = host.split(".");
    if (labels.length < 3) {
      return null;
    }
    return labels.slice(-3).join(".");
  } catch {
    return null;
  }
}

function readCookie(name: string): string | null {
  try {
    const prefix = `${name}=`;
    for (const part of document.cookie.split("; ")) {
      if (part.startsWith(prefix)) {
        // An empty value is a cleared/absent selection — treat it as none.
        return decodeURIComponent(part.slice(prefix.length)) || null;
      }
    }
  } catch {
    // document.cookie unavailable — fall through to null.
  }
  return null;
}

function writeCookie(name: string, value: string | null, domain: string): void {
  try {
    const attrs = `Domain=${domain}; Path=/; Secure; SameSite=Lax`;
    if (value) {
      // ~1 year, so the shared selection outlives typical preview churn.
      document.cookie = `${name}=${encodeURIComponent(value)}; ${attrs}; Max-Age=31536000`;
    } else {
      document.cookie = `${name}=; ${attrs}; Max-Age=0`;
    }
  } catch {
    // Cookies unavailable — selection just won't persist across previews.
  }
}

/** The last org id the user selected, or `null` if none has been chosen. */
export function getPersistedOrgId(): string | null {
  const cookieDomain = pagesProjectCookieDomain();
  if (cookieDomain) {
    return readCookie(KEY);
  }
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

/** Persist the selected org id (or clear it when `null`). */
export function setPersistedOrgId(orgId: string | null): void {
  const cookieDomain = pagesProjectCookieDomain();
  if (cookieDomain) {
    writeCookie(KEY, orgId, cookieDomain);
    return;
  }
  try {
    if (orgId) {
      localStorage.setItem(KEY, orgId);
    } else {
      localStorage.removeItem(KEY);
    }
  } catch {
    // Storage unavailable (private mode / blocked) — selection just won't
    // persist across reloads; the in-memory context still works for the session.
  }
}
